'use client'

import { formatTimeTo12Hour, formatTimeTo24Hour } from '@repo/sdk'
import { cn } from '@repo/ui/lib'
import { XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '.'

interface IProps {
  isClearable?: boolean
  label?: string
  onChange: (time: string) => void
  value: null | string | undefined
}

export const TimeInput = ({ isClearable = false, label, onChange, value }: IProps) => {
  const [selected12Time, setSelected12Time] = useState(formatTimeTo12Hour(value))
  const [selected24Time, setSelected24Time] = useState<string | undefined>()

  // Setting initial period to AM or PM
  const initialPeriod = value?.includes('PM') ? 'PM' : 'AM'
  const [period, setPeriod] = useState<'' | 'AM' | 'PM'>(initialPeriod)

  const [isOpen, setIsOpen] = useState(false)

  const hours = [...Array.from({ length: 11 }, (temp, i) => String(i + 1).padStart(2, '0')), '12']
  const minutes = ['00', '15', '30', '45']

  // On value change, set the selected12Time and period
  useEffect(() => {
    if (value) {
      setSelected12Time(formatTimeTo12Hour(value))
      setPeriod(value.includes('PM') ? 'PM' : 'AM')
    } else {
      setSelected12Time('')
      setPeriod('AM')
    }
  }, [value])

  const setTime = (time: string, period: '' | 'AM' | 'PM') => {
    const formattedTime = formatTimeTo24Hour(time, period)
    setSelected12Time(formatTimeTo12Hour(formattedTime))
    setPeriod(period)
    setSelected24Time(formattedTime)
    onChange(formattedTime)
  }

  const onSave = () => {
    onChange(selected24Time ?? '')
    setIsOpen(false)
  }

  const handleTimeChange = (time: { hour?: string; minute?: string }) => {
    const [currentHour, currentMinute] = selected12Time.split(/[ :]/)
    const hour = time.hour ?? currentHour ?? '12'
    const minute = time.minute ?? currentMinute ?? '00'
    const newTime = `${hour}:${minute}`
    setTime(newTime, period)

    popoverRef.current?.focus()
    setIsOpen(true)
  }

  const handlePeriodChange = (newPeriod: 'AM' | 'PM') => {
    const [time] = selected12Time.split(' ')
    const hourMinute = time || '12:00'
    setTime(hourMinute, newPeriod)

    popoverRef.current?.focus()
    setIsOpen(true)
  }

  const popoverRef = useRef<HTMLDivElement>(null)

  const onClear = () => {
    setPeriod('')
    onChange('')
    setIsOpen(false)
  }

  return (
    <>
      <div className="flex justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Popover onOpenChange={setIsOpen} open={isOpen}>
                <PopoverTrigger>
                  <div className="relative">
                    <Input
                      className="w-32 text-center"
                      placeholder="Select time"
                      readOnly
                      value={selected12Time}
                    />
                    {isClearable && selected12Time.length > 0 && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <XCircle
                          className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
                          onClick={onClear}
                        />
                      </div>
                    )}
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  className="z-[14001] w-auto rounded-md border border-gray-200 bg-white p-0 shadow-md dark:border-gray-700 dark:bg-gray-900"
                  ref={popoverRef}
                >
                  <div className="flex flex-col">
                    <div className="flex justify-center">
                      <div className="scrollbar-hide max-h-52 overflow-y-scroll p-2">
                        <ul className="space-y-1">
                          {hours.map(hour => (
                            <li
                              className={cn(
                                'cursor-pointer rounded-md p-2 hover:bg-blue-200 hover:text-black',
                                selected12Time.startsWith(hour)
                                  ? 'bg-blue-100 text-black'
                                  : 'text-gray-700 dark:text-gray-300',
                              )}
                              key={hour}
                              onClick={() => handleTimeChange({ hour })}
                            >
                              {hour}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="scrollbar-hide max-h-52 overflow-y-scroll border-l border-gray-200 p-2 dark:border-gray-700">
                        <ul className="space-y-1">
                          {minutes.map(minute => (
                            <li
                              className={cn(
                                'cursor-pointer rounded-md p-2 hover:bg-blue-200 hover:text-black',
                                selected12Time.includes(`:${minute}`)
                                  ? 'bg-blue-100 text-black'
                                  : 'text-gray-700 dark:text-gray-300',
                              )}
                              key={minute}
                              onClick={() => handleTimeChange({ minute })}
                            >
                              {minute}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="scrollbar-hide max-h-52 overflow-y-scroll border-l border-gray-200 p-2 dark:border-gray-700">
                        <ul className="space-y-1">
                          {['AM', 'PM'].map(p => (
                            <li
                              className={cn(
                                'cursor-pointer rounded-md p-2 hover:bg-blue-200 hover:text-black',
                                period === p
                                  ? 'bg-blue-100 text-black'
                                  : 'text-gray-700 dark:text-gray-300',
                              )}
                              key={p}
                              onClick={() => handlePeriodChange(p as 'AM' | 'PM')}
                            >
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-end border-t border-gray-200 p-2 dark:border-gray-700">
                      <Button className="text-blue-500" onClick={onSave} variant="ghost">
                        Ok
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </TooltipTrigger>
            <TooltipContent>
              <p>{label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </>
  )
}
